<template>
  <div class="stock-list-wrapper">
    <!-- Toast Notifications -->
    <CToaster :autohide="3000" class="toast-container">
      <CToast
        v-for="info in infoList"
        :key="info.id"
        :show="true"
        :header="info.header"
        :color="info.color"
      >
        {{ info.message }}.
      </CToast>
    </CToaster>

    <!-- Active Filters Section -->
    <div class="active-filters-section" v-if="hasActiveFilters">
      <div class="filters-header d-flex justify-content-between align-items-center">
        <h6 class="mb-2">Active Filters:</h6>
        <CButton size="sm" color="secondary" variant="ghost" @click="onCleanerClick">
          <i class="fas fa-times-circle me-1"></i>
          Clear All
        </CButton>
      </div>
      <div class="filters-badges">
        <CBadge
          v-if="columnFilterValue.stockNo"
          color="primary"
          class="filter-badge"
        >
          Stock No: {{ columnFilterValue.stockNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.stockNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.lotNo"
          color="primary"
          class="filter-badge"
        >
          Loc No: {{ columnFilterValue.lotNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.lotNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.chasisNo"
          color="primary"
          class="filter-badge"
        >
          Chassis No: {{ columnFilterValue.chasisNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.chasisNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.brandModelName"
          color="primary"
          class="filter-badge"
        >
          Brand/Model: {{ columnFilterValue.brandModelName }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.brandModelName = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="priceMinFilter || priceMaxFilter"
          color="primary"
          class="filter-badge"
        >
          Price: {{ priceMinFilter || "0" }} - {{ priceMaxFilter || "∞" }}
          <CButton
            close
            size="sm"
            @click="clearPriceFilter"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="yearFromFilter || yearToFilter || monthFromFilter || monthToFilter"
          color="primary"
          class="filter-badge"
        >
          Date Range: {{ formatDateRange() }}
          <CButton
            close
            size="sm"
            @click="clearDateFilter"
            class="filter-close"
          ></CButton>
        </CBadge>
      </div>
    </div>

    <!-- Enhanced Filter Section -->
    <CRow class="mb-4">
      <CCol>
        <CCard class="filter-card">
          <CCardHeader class="filter-header" @click="toggleFilters">
            <div class="d-flex justify-content-between align-items-center">
              <div class="header-content">
                <i class="fas fa-filter me-2 text-primary"></i>
                <strong>Filters</strong>
                <i
                  :class="filtersExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  class="ms-2 text-muted"
                ></i>
              </div>
              <CBadge color="secondary" class="filter-count">
                {{ computedItems.length }} Results
              </CBadge>
            </div>
          </CCardHeader>
          <CCardBody class="filter-body" v-show="filtersExpanded">
            <!-- Search Filters Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-search me-2"></i>
                Search Filters
              </h6>
              <CRow class="g-3">
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Stock No"
                    v-model="columnFilterValue.stockNo"
                    placeholder="e.g. SA001"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Loc No"
                    v-model="columnFilterValue.lotNo"
                    placeholder="e.g. SA1"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Chassis No"
                    v-model="columnFilterValue.chasisNo"
                    placeholder="e.g. GGH30"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Brand / Model"
                    v-model="columnFilterValue.brandModelName"
                    placeholder="e.g. Toyota Alphard"
                  />
                </CCol>
              </CRow>
            </div>

            <!-- Month/Year Range Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-calendar-alt me-2"></i>
                Vehicle Month/Year Range
              </h6>
              <CRow class="g-3">
                <CCol md="3" cols="6">
                  <CInput
                    label="From Month"
                    v-model="monthFromFilter"
                    placeholder="e.g. 1"
                    type="number"
                    min="1"
                    max="12"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="From Year"
                    v-model="yearFromFilter"
                    placeholder="e.g. 2020"
                    type="number"
                    min="1900"
                    max="2100"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="To Month"
                    v-model="monthToFilter"
                    placeholder="e.g. 12"
                    type="number"
                    min="1"
                    max="12"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="To Year"
                    v-model="yearToFilter"
                    placeholder="e.g. 2024"
                    type="number"
                    min="1900"
                    max="2100"
                  />
                </CCol>
              </CRow>
            </div>

            <!-- Price Range Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-dollar-sign me-2"></i>
                Price Range (MYR)
              </h6>
              <CRow class="g-3">
                <CCol md="6" cols="12">
                  <CInput
                    label="Minimum Price"
                    v-model="priceMinFilter"
                    placeholder="e.g. 20,000"
                    type="number"
                  />
                </CCol>
                <CCol md="6" cols="12">
                  <CInput
                    label="Maximum Price"
                    v-model="priceMaxFilter"
                    placeholder="e.g. 500,000"
                    type="number"
                  />
                </CCol>
              </CRow>
            </div>

            <div class="filter-actions">
              <CButton
                color="secondary"
                variant="outline"
                @click="onCleanerClick"
                class="me-2"
              >
                <i class="fas fa-times me-2"></i>
                Clear
              </CButton>
              <CButton
                color="primary"
                @click="refreshTable"
                :disabled="loading"
              >
                <i class="fas fa-search me-2"></i>
                {{ loading ? 'Searching...' : 'Search' }}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Enhanced Stock List -->
    <CRow>
      <CCol sm="12">
        <CCard class="stock-list-card">
          <CCardHeader class="list-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div class="header-content">
                <i class="fas fa-list me-2 text-primary"></i>
                <strong>Stock List</strong>
                <span 
                  class="info-tooltip ms-2"
                  v-c-tooltip.hover="{
                    content: 'This table shows all stocks. Click on a row to view more details.'
                  }"
                >
                  <i class="fas fa-info-circle text-muted"></i>
                </span>
              </div>
              <div class="status-summary d-flex flex-wrap gap-2">
                <CBadge color="success" class="status-badge">
                  {{ statusCounts.open }} Available
                </CBadge>
                <CBadge color="warning" class="status-badge">
                  {{ statusCounts.booked }} Booked
                </CBadge>
                <CBadge color="danger" class="status-badge">
                  {{ statusCounts.sold }} Sold
                </CBadge>
              </div>
            </div>
          </CCardHeader>
          <CCardBody class="list-body p-0">
            <!-- Mobile Card View (visible on small screens) -->
            <div class="mobile-view d-md-none">
              <CRow class="mb-3">
                <CCol xs="12">
                  <CSelect
                    :value.sync="itemsPerPage"
                    :options="itemsPerPageOptions"
                    @update:value="onItemsPerPageChange"
                    label="Items per page"
                    size="sm"
                  />
                </CCol>
              </CRow>
              <div v-if="loading" class="loading-state">
                <div class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading stock data...</p>
                </div>
              </div>
              
              <div v-else-if="computedItems.length === 0" class="empty-state">
                <div class="text-center py-5">
                  <i class="fas fa-box-open fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No stock found</h5>
                  <p class="text-muted">Try adjusting your search criteria.</p>
                </div>
              </div>

              <div v-else class="mobile-cards">
                <div 
                  v-for="(item, index) in computedItems" 
                  :key="item.id"
                  class="mobile-card"
                  @click="handleRowClick(item, index)"
                >
                  <div class="mobile-card-header">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="stock-info">
                        <h6 class="mb-1">{{ item.brandModelName }}</h6>
                        <small class="text-muted">
                          Stock: {{ item.stockNo }} • Lot: {{ item.lotNo }}
                          <template v-if="item.chasisNo"> • Chassis: {{ item.chasisNo }}</template>
                        </small>
                      </div>
                      <div class="mobile-image">
                        <img
                          v-if="getImage(item)"
                          :src="getImage(item)"
                          :alt="item.brandModelName"
                          class="stock-image"
                        />
                        <div v-else class="no-image">
                          <i class="fas fa-image"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mobile-card-body">
                    <div class="row g-2">
                      <div class="col-6">
                        <small class="text-muted d-block">Price</small>
                        <strong class="text-success">{{ item.price }}</strong>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Record Date</small>
                        <span>{{ formatMovedToStockDate(item) }}</span>
                      </div>
                    </div>

                    <div class="row g-2 mt-2">
                      <div class="col-6">
                        <small class="text-muted d-block">Year</small>
                        <span>{{ item.yearMonth }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Loc No</small>
                        <span>{{ item.lotNo }}</span>
                      </div>
                    </div>
                    
                    <div class="row g-2 mt-2">
                      <div class="col-6">
                        <small class="text-muted d-block">Arrival</small>
                        <CBadge :color="getArrivalBadge(item.arrivalStatus)">
                          <i :class="item.arrivalStatus === 'Received' ? 'fas fa-check-circle me-1' : 'fas fa-shipping-fast me-1'"></i>
                          {{ item.arrivalStatus }}
                        </CBadge>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Status</small>
                        <div class="d-flex flex-wrap gap-1">
                          <CBadge
                            v-if="item.stockStatus.includes('Open')"
                            color="success"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-check-circle me-1"></i>
                            Available
                          </CBadge>
                          <CBadge
                            v-if="item.stockStatus.includes('LOU')"
                            color="info"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-file-contract me-1"></i>
                            LOU
                          </CBadge>
                          <CBadge
                            v-if="item.stockStatus.includes('Booking')"
                            color="warning"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-calendar-check me-1"></i>
                            Booking
                          </CBadge>
                          <CBadge
                            v-if="item.stockStatus.includes('Sold')"
                            color="danger"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-handshake me-1"></i>
                            Sold
                          </CBadge>
                          <CBadge
                            v-if="item.stockStatus.includes('Cancelled')"
                            color="secondary"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-ban me-1"></i>
                            Cancelled
                          </CBadge>
                          <CBadge
                            v-if="item.stockStatus.includes('Request Duty')"
                            color="warning"
                            class="mobile-status-badge"
                          >
                            <i class="fas fa-file-invoice me-1"></i>
                            Request Duty
                          </CBadge>
                        </div>
                      </div>
                    </div>

                    <!-- State Badges -->
                    <div class="mt-2" v-if="item.stockState">
                      <div class="d-flex flex-wrap gap-1">
                        <CBadge
                          v-if="item.stockState.includes('NEW_STOCK')"
                          color="light"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-star me-1"></i>
                          NEW STOCK
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRED')"
                          color="danger"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-times-circle me-1"></i>
                          BOOKING EXPIRED
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRY')"
                          color="warning"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-exclamation-triangle me-1"></i>
                          BOOKING EXPIRY
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('PRICE_CHANGE')"
                          color="info"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-tag me-1"></i>
                          NEW PRICE
                        </CBadge>
                      </div>
                    </div>

                    <!-- Description -->
                    <div class="mt-2" v-if="item.vehicleDescription">
                      <small class="text-muted d-block">Description</small>
                      <small>{{ truncateText(item.vehicleDescription, 80) }}</small>
                    </div>
                  </div>

                  <div class="mobile-card-footer">
                    <small class="text-muted">
                      Tap to {{ item._toggled ? 'collapse' : 'expand' }}
                    </small>
                  </div>

                  <!-- Mobile Details Expansion -->
                  <CCollapse :show="Boolean(item._toggled)" :duration="collapseDuration">
                    <!-- Loading spinner while details are rendering -->
                    <div v-if="expandingItemId === item.id" class="details-loading text-center py-4">
                      <CSpinner color="primary" />
                      <p class="mt-3 text-muted">Loading details...</p>
                    </div>

                    <!-- Actual details content - only render when ready -->
                    <div v-else-if="item._detailsReady" class="mobile-details">
                      <hr class="my-3">
                      <div class="mobile-details-content">
                        <div class="detail-info">
                          <p class="text-muted mb-2">Stock No: {{ item.stockNo }}</p>
                          <p class="text-muted mb-2">Status: {{ item.latestStatus }}</p>
                          <p class="text-muted mb-2">Brand: {{ item.brandName }}</p>
                          <p class="text-muted mb-2">Model: {{ item.modelName }}</p>
                          <p class="text-muted mb-3">Price: {{ item.price }}</p>
                        </div>

                        <!-- Process Status Indicators (Mobile) -->
                        <div class="process-status-section mb-3">
                          <h6 class="mb-2">
                            <i class="fas fa-tasks me-2"></i>PROGRESS
                          </h6>
                          <div class="process-status-list">
                            <div class="process-status-row">
                              <span class="status-label">AP MITI:</span>
                              <CBadge :color="item.statusApMiti.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusApMiti.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusApMiti.completed" class="status-description">
                                {{ item.statusApMiti.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">BL:</span>
                              <CBadge :color="item.statusBl.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusBl.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusBl.completed" class="status-description">
                                {{ item.statusBl.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">JK69:</span>
                              <CBadge :color="item.statusJk69.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusJk69.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusJk69.completed" class="status-description">
                                {{ item.statusJk69.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">K1/K8:</span>
                              <CBadge :color="item.statusK1K8.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusK1K8.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusK1K8.completed" class="status-description">
                                {{ item.statusK1K8.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">FLOOR STOCK:</span>
                              <CBadge :color="item.statusFloorStock.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusFloorStock.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusFloorStock.completed" class="status-description">
                                {{ item.statusFloorStock.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">TT:</span>
                              <CBadge :color="item.statusTt.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusTt.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusTt.completed" class="status-description">
                                {{ item.statusTt.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">DUTY TAX:</span>
                              <CBadge :color="item.statusDutyTax.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusDutyTax.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusDutyTax.completed" class="status-description">
                                {{ item.statusDutyTax.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">PUSPAKOM:</span>
                              <CBadge :color="item.statusPuspakom.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusPuspakom.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusPuspakom.completed" class="status-description">
                                {{ item.statusPuspakom.message }}
                              </span>
                            </div>

                            <div class="process-status-row">
                              <span class="status-label">REGISTER:</span>
                              <CBadge :color="item.statusRegister.completed ? 'success' : 'warning'" class="status-badge">
                                {{ item.statusRegister.completed ? 'COMPLETED' : 'PENDING' }}
                              </CBadge>
                              <span v-if="!item.statusRegister.completed" class="status-description">
                                {{ item.statusRegister.message }}
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Mobile Document Links -->
                        <div v-if="hasDocuments(item)" class="mb-3">
                          <h6 class="mb-2">
                            <i class="fas fa-file-pdf me-2"></i>Documents
                          </h6>
                          <div class="document-links">
                            <div v-if="item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length">
                              <small class="text-muted d-block mb-1">K8 Documents:</small>
                              <div class="d-flex flex-wrap gap-1">
                                <a
                                  v-for="doc in item.clearance.k8Documents"
                                  :key="doc.id"
                                  :href="getDocumentUrl(doc)"
                                  target="_blank"
                                  class="btn btn-outline-primary btn-sm me-1 mb-1"
                                >
                                  <i class="fas fa-download me-1"></i>
                                  {{ getDocumentName(doc) }}
                                </a>
                              </div>
                            </div>
                            <div v-if="item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length" class="mt-2">
                              <small class="text-muted d-block mb-1">BOL Documents:</small>
                              <div class="d-flex flex-wrap gap-1">
                                <a
                                  v-for="doc in item.import.billOfLandingDocuments"
                                  :key="doc.id"
                                  :href="getDocumentUrl(doc)"
                                  target="_blank"
                                  class="btn btn-outline-primary btn-sm me-1 mb-1"
                                >
                                  <i class="fas fa-download me-1"></i>
                                  {{ getDocumentName(doc) }}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Edit + open-in-new-tab are paired ("open the same
                             page") so we visually group them; Delete is
                             destructive and stays separated. -->
                        <div class="mobile-actions d-flex flex-wrap gap-2 align-items-center">
                          <CButtonGroup size="sm">
                            <CButton
                              :color="canUpdate ? 'primary' : 'info'"
                              @click.stop="onEdit(item)"
                            >
                              <CIcon :name="canUpdate ? 'cil-pencil' : 'cil-notes'" class="mr-1" />
                              {{ canUpdate ? 'Edit' : 'View' }}
                            </CButton>
                            <CButton
                              :color="canUpdate ? 'primary' : 'info'"
                              variant="outline"
                              title="Open in new tab"
                              @click.stop="onEditInNewTab(item)"
                            >
                              <CIcon name="cil-share-boxed" />
                            </CButton>
                          </CButtonGroup>
                          <CButton
                            v-if="canDelete"
                            size="sm"
                            color="danger"
                            variant="outline"
                            @click.stop="showDeleteConfirmation(item)"
                          >
                            <CIcon name="cil-trash" class="mr-1" />
                            Delete
                          </CButton>
                        </div>
                      </div>
                    </div>
                    <!-- End of v-else mobile-details -->
                  </CCollapse>
                </div>
              </div>
            </div>

            <!-- Desktop Table View (hidden on small screens) -->
            <div class="desktop-view d-none d-md-block">
              <CRow class="mb-3 px-3 pt-3 align-items-center">
                <CCol sm="8">
                  <!-- Existing filter/search content can stay here if any -->
                </CCol>
                <CCol sm="4" class="text-right">
                  <div class="d-flex align-items-center justify-content-end">
                    <label class="mb-0 mr-2" style="white-space: nowrap;">Items per page:</label>
                    <CSelect
                      :value.sync="itemsPerPage"
                      :options="itemsPerPageOptions"
                      @update:value="onItemsPerPageChange"
                      style="width: 150px;"
                      size="sm"
                    />
                  </div>
                </CCol>
              </CRow>
              <div class="table-responsive">
                <CDataTable
                  :items="computedItems"
                  :fields="enhancedFields"
                  :column-filter="{ external: true, lazy: true }"
                  :column-filter-value.sync="columnFilterValue"
                  :sorter="{ external: true, resetable: true }"
                  :sorter-value.sync="sorterValue"
                  :items-per-page="itemsPerPage"
                  :active-page="1"
                  hover
                  :loading="loading"
                  @row-clicked="handleRowClick"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">{{ index + 1 }}</span>
                    </td>
                  </template>

                  <template #stockState="{ item }">
                    <td>
                      <div class="d-flex flex-column gap-1">
                        <CBadge
                          v-if="item.stockState.includes('NEW_STOCK')"
                          color="light"
                          class="state-badge"
                        >
                          <i class="fas fa-star me-1"></i>
                          NEW STOCK
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRED')"
                          color="danger"
                          class="state-badge"
                        >
                          <i class="fas fa-times-circle me-1"></i>
                          BOOKING EXPIRED
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRY')"
                          color="warning"
                          class="state-badge"
                        >
                          <i class="fas fa-exclamation-triangle me-1"></i>
                          BOOKING EXPIRY
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('PRICE_CHANGE')"
                          color="info"
                          class="state-badge"
                        >
                          <i class="fas fa-tag me-1"></i>
                          NEW PRICE
                        </CBadge>
                      </div>
                    </td>
                  </template>

                  <template #stockNo="{ item }">
                    <td>
                      <strong>{{ item.stockNo }}</strong>
                    </td>
                  </template>

                  <template #movedToStockAt="{ item }">
                    <td>
                      <span class="text-muted">{{ formatMovedToStockDate(item) }}</span>
                    </td>
                  </template>

                  <template #lotNo="{ item }">
                    <td>
                      <span>{{ item.lotNo }}</span>
                    </td>
                  </template>

                  <template #yearMonth="{ item }">
                    <td>
                      <span>{{ item.yearMonth }}</span>
                    </td>
                  </template>

                  <template #arrivalStatus="{ item }">
                    <td>
                      <CBadge :color="getArrivalBadge(item.arrivalStatus)">
                        <i :class="item.arrivalStatus === 'Received' ? 'fas fa-check-circle me-1' : 'fas fa-shipping-fast me-1'"></i>
                        {{ item.arrivalStatus }}
                      </CBadge>
                    </td>
                  </template>

                  <template #eta="{ item }">
                    <td>
                      <small>{{ item.eta }}</small>
                    </td>
                  </template>

                  <template #stockStatus="{ item }">
                    <td>
                      <div class="d-flex flex-wrap gap-1">
                        <CBadge
                          v-if="item.stockStatus.includes('Open')"
                          color="success"
                          class="status-badge"
                        >
                          <i class="fas fa-check-circle me-1"></i>
                          Available
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('LOU')"
                          color="info"
                          class="status-badge"
                        >
                          <i class="fas fa-file-contract me-1"></i>
                          LOU
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Booking')"
                          color="warning"
                          class="status-badge"
                        >
                          <i class="fas fa-calendar-check me-1"></i>
                          Booking
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Sold')"
                          color="danger"
                          class="status-badge"
                        >
                          <i class="fas fa-handshake me-1"></i>
                          Sold
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Cancelled')"
                          color="secondary"
                          class="status-badge"
                        >
                          <i class="fas fa-ban me-1"></i>
                          Cancelled
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Request Duty')"
                          color="warning"
                          class="status-badge"
                        >
                          <i class="fas fa-file-invoice me-1"></i>
                          Request Duty
                        </CBadge>
                      </div>
                    </td>
                  </template>

                  <template #brandModelName="{ item }">
                    <td>
                      <strong>{{ item.brandModelName }}</strong>
                    </td>
                  </template>

                  <template #image="{ item }">
                    <td class="text-center">
                      <img
                        v-if="getImage(item)"
                        :src="getImage(item)"
                        :alt="item.brandModelName"
                        class="table-thumbnail"
                      />
                      <div v-else class="no-thumbnail">
                        <i class="fas fa-image text-muted"></i>
                      </div>
                    </td>
                  </template>

                  <template #price="{ item }">
                    <td>
                      <strong class="text-success">{{ item.price }}</strong>
                    </td>
                  </template>

                  <template #vehicleDescription="{ item }">
                    <td>
                      <span :title="item.vehicleDescription">
                        {{ truncateText(item.vehicleDescription, 50) }}
                      </span>
                    </td>
                  </template>

                  <template #details="{ item }">
                    <CCollapse
                      v-if="item._toggled"
                      :show="Boolean(item._toggled)"
                      :duration="collapseDuration"
                    >
                      <!-- Loading spinner while details are rendering -->
                      <div v-if="expandingItemId === item.id" class="details-loading text-center py-5">
                        <CSpinner color="primary" />
                        <p class="mt-3 text-muted">Loading details...</p>
                      </div>

                      <!-- Actual details content - only render when ready -->
                      <div v-else-if="item._detailsReady" class="details-wrapper">
                        <CCard class="details-card">
                          <CCardHeader>
                            <h6 class="mb-0">
                              <i class="fas fa-info-circle me-2"></i>
                              Stock Details - {{ item.stockNo }}
                            </h6>
                          </CCardHeader>
                          <CCardBody>
                            <CRow>
                              <CCol md="8">
                                <div class="detail-info">
                                  <p class="text-muted mb-2">Stock No: {{ item.stockNo }}</p>
                                  <p class="text-muted mb-2">Status: {{ item.latestStatus }}</p>
                                  <p class="text-muted mb-2">Brand: {{ item.brandName }}</p>
                                  <p class="text-muted mb-2">Model: {{ item.modelName }}</p>
                                  <p class="text-muted mb-3">Price: {{ item.price }}</p>
                                </div>

                                <!-- Process Status Indicators -->
                                <div class="process-status-section mb-3">
                                  <h6 class="mb-2">
                                    <i class="fas fa-tasks me-2"></i>PROGRESS
                                  </h6>
                                  <div class="process-status-list">
                                    <div class="process-status-row">
                                      <span class="status-label">AP MITI:</span>
                                      <CBadge :color="item.statusApMiti.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusApMiti.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusApMiti.completed" class="status-description">
                                        {{ item.statusApMiti.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">BL:</span>
                                      <CBadge :color="item.statusBl.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusBl.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusBl.completed" class="status-description">
                                        {{ item.statusBl.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">JK69:</span>
                                      <CBadge :color="item.statusJk69.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusJk69.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusJk69.completed" class="status-description">
                                        {{ item.statusJk69.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">K1/K8:</span>
                                      <CBadge :color="item.statusK1K8.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusK1K8.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusK1K8.completed" class="status-description">
                                        {{ item.statusK1K8.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">FLOOR STOCK:</span>
                                      <CBadge :color="item.statusFloorStock.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusFloorStock.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusFloorStock.completed" class="status-description">
                                        {{ item.statusFloorStock.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">TT:</span>
                                      <CBadge :color="item.statusTt.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusTt.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusTt.completed" class="status-description">
                                        {{ item.statusTt.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">DUTY TAX:</span>
                                      <CBadge :color="item.statusDutyTax.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusDutyTax.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusDutyTax.completed" class="status-description">
                                        {{ item.statusDutyTax.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">PUSPAKOM:</span>
                                      <CBadge :color="item.statusPuspakom.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusPuspakom.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusPuspakom.completed" class="status-description">
                                        {{ item.statusPuspakom.message }}
                                      </span>
                                    </div>

                                    <div class="process-status-row">
                                      <span class="status-label">REGISTER:</span>
                                      <CBadge :color="item.statusRegister.completed ? 'success' : 'warning'" class="status-badge">
                                        {{ item.statusRegister.completed ? 'COMPLETED' : 'PENDING' }}
                                      </CBadge>
                                      <span v-if="!item.statusRegister.completed" class="status-description">
                                        {{ item.statusRegister.message }}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Document Links -->
                                <CRow v-if="hasDocuments(item)" class="mb-3">
                                  <CCol md="6" v-if="item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length">
                                    <div class="document-section">
                                      <h6>
                                        <i class="fas fa-file-pdf me-2"></i>K8 Documents
                                      </h6>
                                      <div class="document-list">
                                        <div
                                          v-for="doc in item.clearance.k8Documents"
                                          :key="doc.id"
                                          class="mb-2"
                                        >
                                          <a
                                            :href="getDocumentUrl(doc)"
                                            target="_blank"
                                            class="btn btn-outline-primary btn-sm"
                                          >
                                            <i class="fas fa-download me-2"></i>
                                            {{ getDocumentName(doc) }}
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  </CCol>

                                  <CCol md="6" v-if="item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length">
                                    <div class="document-section">
                                      <h6>
                                        <i class="fas fa-file-pdf me-2"></i>BOL Documents
                                      </h6>
                                      <div class="document-list">
                                        <div
                                          v-for="doc in item.import.billOfLandingDocuments"
                                          :key="doc.id"
                                          class="mb-2"
                                        >
                                          <a
                                            :href="getDocumentUrl(doc)"
                                            target="_blank"
                                            class="btn btn-outline-primary btn-sm"
                                          >
                                            <i class="fas fa-download me-2"></i>
                                            {{ getDocumentName(doc) }}
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  </CCol>
                                </CRow>

                                <!-- Edit + open-in-new-tab are paired so they
                                     share a button group; Delete is destructive
                                     and visually separated. -->
                                <div class="action-buttons d-flex flex-wrap align-items-center" style="gap: 0.5rem;">
                                  <CButtonGroup size="sm">
                                    <CButton
                                      :color="canUpdate ? 'primary' : 'info'"
                                      @click="onEdit(item)"
                                    >
                                      <CIcon :name="canUpdate ? 'cil-pencil' : 'cil-notes'" class="mr-1" />
                                      {{ canUpdate ? 'Edit' : 'View' }}
                                    </CButton>
                                    <CButton
                                      :color="canUpdate ? 'primary' : 'info'"
                                      variant="outline"
                                      title="Open in new tab"
                                      @click="onEditInNewTab(item)"
                                    >
                                      <CIcon name="cil-share-boxed" />
                                    </CButton>
                                  </CButtonGroup>
                                  <CButton
                                    v-if="canDelete"
                                    size="sm"
                                    color="danger"
                                    variant="outline"
                                    @click="showDeleteConfirmation(item)"
                                  >
                                    <CIcon name="cil-trash" class="mr-1" />
                                    Delete
                                  </CButton>
                                </div>
                              </CCol>
                              <CCol md="4" v-if="getImage(item)">
                                <div class="detail-image">
                                  <img
                                    :src="getImage(item)"
                                    alt="Car Image"
                                    class="detail-car-image"
                                  />
                                </div>
                              </CCol>
                            </CRow>
                          </CCardBody>
                        </CCard>
                      </div>
                      <!-- End of v-else details-wrapper -->
                    </CCollapse>
                  </template>
                </CDataTable>
              </div>

              <!-- Pagination -->
              <div class="pagination-wrapper" v-show="pages > 1">
                <CPagination
                  :pages="pages"
                  :active-page.sync="activePage"
                  @update:active-page="refreshTable"
                />
              </div>
            </div>
          </CCardBody>
          
          <!-- Enhanced Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton
                  v-if="canAdd"
                  color="primary"
                  @click="addNew"
                  class="me-2"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New
                </CButton>
                <CButton
                  color="success"
                  variant="outline"
                  :disabled="!!downloadingCsv"
                  @click="downloadCsvFiltered"
                  class="me-2"
                >
                  <i class="fas fa-download me-2"></i>
                  {{ downloadingCsv === 'filtered' ? 'Preparing...' : 'Download CSV' }}
                </CButton>
                <CButton
                  color="info"
                  variant="outline"
                  :disabled="!!downloadingCsv"
                  @click="downloadCsvAll"
                >
                  <i class="fas fa-download me-2"></i>
                  {{ downloadingCsv === 'all' ? 'Preparing...' : 'Download All' }}
                </CButton>
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Showing {{ computedItems.length }} of {{ totalItems }} stocks
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>

        <!-- Delete Confirmation Modal -->
        <CModal
          title="Confirm Delete"
          color="warning"
          :show.sync="warningModal"
        >
          <div class="modal-content-wrapper">
            <div class="text-center mb-3">
              <i class="fas fa-exclamation-triangle fa-3x text-warning"></i>
            </div>
            <p class="text-center">
              Are you sure you want to delete this stock item?
            </p>
            <div class="alert alert-warning" role="alert">
              <strong>Warning:</strong> This action cannot be undone.
            </div>
          </div>
          <template #footer>
            <CButton color="secondary" @click="warningModal = false">
              Cancel
            </CButton>
            <CButton color="danger" @click="onDeleteConfirmation">
              <i class="fas fa-trash me-2"></i>
              Delete
            </CButton>
          </template>
        </CModal>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import StockApi from "../../lib/stockApi";
import moment from "moment";
import { formatMalaysiaDate, formatMalaysiaDateTime } from "@/utils/dateUtil";

// Define apiUrl - replace with your actual API URL
const apiUrl = window.config?.API_URL || process.env.VUE_APP_API_URL || 'https://localhost:44305/api/';

export default {
  name: "StockList",
  data() {
    return {
      loadedItems: [],
      downloadingCsv: null, // null | 'filtered' | 'all' — drives the two CSV export buttons

      // Pre-init filter keys so Vue 2 reactivity tracks them from first mount.
      // Without this, the first character a user types into a panel input
      // creates a brand-new property that hasActiveFilters can't see, so the
      // active-filter chip never appears.
      columnFilterValue: {
        stockNo: "",
        lotNo: "",
        chasisNo: "",
        brandModelName: "",
      },
      tableFilterValue: "",
      sorterValue: { column: "movedToStockAt", asc: false },
      // NOTE: the debounce timer handle is stored at runtime as this._filterDebounce
      // (a non-reactive instance property) rather than in data() — a timer id doesn't
      // need reactivity, and declaring a "_"-prefixed data key trips vue/no-reserved-keys.

      // Pagination
      activePage: 1,
      itemsPerPage: 20,
      pages: 1,
      totalItems: 0,
      itemsPerPageOptions: [
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' },
        { value: 100, label: '100 per page' },
        { value: 500, label: '500 per page' },
        { value: 1000, label: '1000 per page' },
        { value: 999999, label: 'Show All' }
      ],

      stockStatusOptions: [
        { value: "Open", text: "Available" },
        { value: "LOU", text: "LOU" },
        { value: "Booking", text: "Booking" },
        { value: "Sold", text: "Sold" },
        { value: "Cancelled", text: "Cancelled" },
        { value: "Request Duty", text: "Request Duty" },
      ],

      // Filter states
      priceMinFilter: "",
      priceMaxFilter: "",
      monthFromFilter: "",
      yearFromFilter: "",
      monthToFilter: "",
      yearToFilter: "",
      filtersExpanded: false,

      loading: true,
      items: [],
      infoList: [],
      
      // Enhanced fields for better display
      enhancedFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "stockState",
          _style: "width:150px",
          label: "State",
          sorter: false,
        },
        {
          key: "stockNo",
          label: "Stock No",
          _style: "width:100px"
        },
        {
          key: "chasisNo",
          label: "Chassis No",
          _style: "width:140px"
        },
        {
          key: "movedToStockAt",
          label: "Record Date",
          _style: "width:150px"
        },
        {
          key: "lotNo",
          label: "Loc No",
          _style: "width:100px"
        },
        {
          key: "yearMonth",
          label: "Year",
          _style: "width:80px"
        },
        {
          key: "arrivalStatus",
          label: "Arrival",
          _style: "width:130px"
        },
        {
          key: "eta",
          label: "ETA",
          _style: "width:100px"
        },
        {
          key: "stockStatus",
          _style: "width:150px",
          label: "Status",
          sorter: false,
        },
        {
          key: "brandModelName",
          label: "Model",
          _style: "min-width:200px"
        },
        {
          key: "price",
          filter: false,
          _style: "width:120px"
        },
        {
          key: "vehicleDescription",
          label: "Description",
          sorter: false,
          _style: "min-width:200px"
        },
        {
          key: "image",
          label: "Image",
          _style: "width:90px",
          sorter: false,
          filter: false,
        },
      ],
      
      details: [],
      collapseDuration: 0,
      api: new StockApi(),
      warningModal: false,
      itemToDelete: {},
      expandingItemId: null, // Track which item is currently expanding
      cachedComputedItems: [], // Cache computed items to avoid re-computation on row clicks
    };
  },

  mounted() {
    this.refreshTable();
  },

  watch: {
    reloadParams() {
      // Debounce while typing (Stock No / Chassis No / Brand-Model / Loc No
      // are reactive, so every keystroke would otherwise fire a backend query).
      clearTimeout(this._filterDebounce);
      this._filterDebounce = setTimeout(() => {
        this.refreshTable();
      }, 350);
    },
  },

  computed: {
    isMobileView() {
      // Check if screen is mobile size (< 768px)
      if (typeof window === 'undefined') return false;
      return window.innerWidth < 768;
    },

    reloadParams() {
      return [
        this.sorterValue,
        this.columnFilterValue,
        this.tableFilterValue,
        this.activePage,
        this.itemsPerPage,
        this.priceMinFilter,
        this.priceMaxFilter,
        this.monthFromFilter,
        this.yearFromFilter,
        this.monthToFilter,
        this.yearToFilter,
      ];
    },
    
    computedItems() {
      // Return cached version to avoid re-computation on every interaction
      return this.cachedComputedItems;
    },

    // Cache status counts to avoid filtering all items multiple times per render
    statusCounts() {
      const counts = { open: 0, booked: 0, sold: 0 };
      this.computedItems.forEach(item => {
        if (item.stockStatus && item.stockStatus.includes('Open')) counts.open++;
        if (item.stockStatus && item.stockStatus.includes('Booking')) counts.booked++;
        if (item.stockStatus && item.stockStatus.includes('Sold')) counts.sold++;
      });
      return counts;
    },

    hasActiveFilters() {
      return !!(
        this.columnFilterValue.stockNo ||
        this.columnFilterValue.lotNo ||
        this.columnFilterValue.chasisNo ||
        this.columnFilterValue.brandModelName ||
        this.priceMinFilter ||
        this.priceMaxFilter ||
        this.yearFromFilter ||
        this.yearToFilter ||
        this.monthFromFilter ||
        this.monthToFilter
      );
    },
    canDelete() {
      return this.$auth.hasModulePermission('STOCKS', 'CanDelete');
    },
    canUpdate() {
      return this.$auth.hasModulePermission('STOCKS', 'CanUpdate');
    },
    canAdd() {
      return this.$auth.hasModulePermission('STOCKS', 'CanAdd');
    },
    canView() {
      return this.$auth.hasModulePermission('STOCKS', 'CanView');
    },
  },

  methods: {
    // Toggle filters (works on all screen sizes)
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    // Utility method for text truncation
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    formatMovedToStockDate(item) {
      if (!item.movedToStockAt) {
        return "01/01/2025 00:00:00";
      }

      // Check if it's already formatted (contains slashes)
      if (typeof item.movedToStockAt === 'string' && item.movedToStockAt.includes('/')) {
        return item.movedToStockAt;
      }

      // Parse UTC date and convert to Malaysia time (GMT+8)
      return formatMalaysiaDateTime(item.movedToStockAt);
    },

    getLotNo(item) {
      if (item.showRoom == null) return "N/A";
      if (item.showRoom.lotNo == null) return "N/A";
      return item.showRoom.lotNo;
    },

    handleRowClick(item, index, event) {
      this.toggleDetails(item, index);
    },

    onCleanerClick() {
      this.priceMinFilter = "";
      this.priceMaxFilter = "";
      this.monthFromFilter = "";
      this.yearFromFilter = "";
      this.monthToFilter = "";
      this.yearToFilter = "";
      this.columnFilterValue = {};
      this.tableFilterValue = "";
      this.refreshTable();
    },

    clearPriceFilter() {
      this.priceMinFilter = "";
      this.priceMaxFilter = "";
      this.refreshTable();
    },

    clearDateFilter() {
      this.monthFromFilter = "";
      this.yearFromFilter = "";
      this.monthToFilter = "";
      this.yearToFilter = "";
      this.refreshTable();
    },

    formatDateRange() {
      const parts = [];
      if (this.yearFromFilter && this.monthFromFilter) {
        parts.push(`From: ${this.monthFromFilter}/${this.yearFromFilter}`);
      } else if (this.yearFromFilter) {
        parts.push(`From: ${this.yearFromFilter}`);
      }

      if (this.yearToFilter && this.monthToFilter) {
        parts.push(`To: ${this.monthToFilter}/${this.yearToFilter}`);
      } else if (this.yearToFilter) {
        parts.push(`To: ${this.yearToFilter}`);
      }

      return parts.length > 0 ? parts.join(" ") : "Date Range";
    },

    getVehicleDescription(item) {
      try {
        return item.vehicle.description;
      } catch (error) {
        return "";
      }
    },

    getBrandModelName(item) {
      try {
        return item.vehicle.brand.name + " " + item.vehicle.model.name;
      } catch (error) {
        return "N/A";
      }
    },

    getArrivalBadge(status) {
      return status === "Received" ? "success" : "info";
    },

    getStockState(item) {
      try {
        let statusArray = [];
        const now = new Date();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

        const registrationDate = new Date(item.registrationDate);

        if (now - registrationDate < sevenDaysInMs) {
          statusArray.push("NEW_STOCK");
        }

        if (item.isBooked) {
          const bookingExpiryDate = new Date(item.sale.bookingExpiryDate);
          if (
            now - bookingExpiryDate < sevenDaysInMs &&
            now - bookingExpiryDate <= 0
          ) {
            statusArray.push("BOOKING_EXPIRY");
          }
          if (now - bookingExpiryDate > 0) {
            statusArray.push("BOOKING_EXPIRED");
          }
        }

        const priceChangeDate = new Date(item.pricing.lastPriceChange);

        if (now - priceChangeDate < sevenDaysInMs) {
          statusArray.push("PRICE_CHANGE");
        }

        return statusArray.length > 0 ? statusArray.join(" ") : "";
      } catch (error) {
        return "";
      }
    },

    getStockStatus(item) {
      let statusArray = [];
      if (item.isOpen) {
        statusArray.push("Open");
      }
      if (item.isLou) {
        statusArray.push("LOU");
      }
      if (item.isBooked) {
        statusArray.push("Booking");
      }
      if (item.isSold) {
        statusArray.push("Sold");
      }
      if (item.isCancelled) {
        statusArray.push("Cancelled");
      }
      if (item.isRequestDuty) {
        statusArray.push("Request Duty");
      }

      return statusArray.length > 0 ? statusArray.join(" ") : "";
    },

    getArrivalStatus(item) {
      if (item.arrivalStateDescription == "Received") return "Received";
      return "In Transit";
    },

    getEtaDate(item) {
      try {
        const importData = item.import || item.Import;
        if (!importData) return "N/A";
        const eta = importData.estimateDateOfArrival || importData.EstimateDateOfArrival;
        if (eta) return formatMalaysiaDate(eta);
        return "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    // Update cached computed items - called only when data changes, not on every interaction
    updateComputedItemsCache() {
      const cacheStartTime = Date.now();
      console.log(`[Performance] Starting computedItems cache update for ${this.loadedItems.length} items`);

      this.cachedComputedItems = this.loadedItems.map((item) => {
        return {
          ...item,
          latestStatus: this.getLatesStockStatusName(item),
          brandName: this.getBrandName(item),
          modelName: this.getModelName(item),
          price: this.getPricing(item),
          brandModelName: this.getBrandModelName(item),
          vehicleDescription: this.getVehicleDescription(item),
          lotNo: this.getLotNo(item),
          // Backend ships chassis as `vehicle.chasisNo` (single-s spelling in
          // the EF model). Flatten it onto the row so the table cell + filter
          // bind to a top-level key like every other column.
          chasisNo: (item.vehicle && item.vehicle.chasisNo) || "",
          yearMonth: this.getMonthYear(item),
          arrivalStatus: this.getArrivalStatus(item),
          eta: this.getEtaDate(item),
          stockStatus: this.getStockStatus(item),
          stockState: this.getStockState(item),
          movedToStockAt: this.formatMovedToStockDate(item),
          // Pre-calculate status checks to avoid re-computing on row expansion
          statusApMiti: this.getApMitiStatus(item),
          statusBl: this.getBlStatus(item),
          statusJk69: this.getJk69Status(item),
          statusK1K8: this.getK1K8Status(item),
          statusFloorStock: this.getFloorStockStatus(item),
          statusTt: this.getTtStatus(item),
          statusDutyTax: this.getDutyTaxStatus(item),
          statusPuspakom: this.getPuspakomStatus(item),
          statusRegister: this.getRegisterStatus(item),
        };
      });

      const cacheEndTime = Date.now();
      console.log(`[Performance] Cache update completed in ${cacheEndTime - cacheStartTime}ms`);
    },

    hasDocuments(item) {
      return (
        (item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length) ||
        (item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length)
      );
    },

    getDocumentName(item) {
      try {
        return item.document?.fileName || "Unknown Document";
      } catch (error) {
        return "Unknown Document";
      }
    },

    getDocumentUrl(item) {
      return `${apiUrl}documents/file/${item.documentId}`;
    },

    getMonthYear(item) {
      var month = "";
      var year = "";
      try {
        month = item.vehicle.month;
      } catch (error) {}
      try {
        year = item.vehicle.year;
      } catch (error) {}

      if (year && month) {
        const paddedMonth = String(month).padStart(2, '0');
        return paddedMonth + "/" + year;
      }
      if (year) {
        return "XX/" + year;
      }
      return "N/A";
    },

    getImage(item) {
      try {
        const baseUrl = apiUrl.endsWith('/') ? apiUrl : apiUrl + '/';

        // 1. Preferred: user-chosen default photo (Vehicle.DefaultImageUrl is set via
        //    the "Default" button on the Vehicle Info tab; format "/Documents/File/{id}").
        //    Extract the docId and route through the resize endpoint for ~170x smaller payload.
        const defaultUrl = item.vehicle?.defaultImageUrl;
        if (defaultUrl) {
          const match = /\/Documents\/File\/([0-9a-fA-F-]{36})/i.exec(defaultUrl);
          if (match) {
            return baseUrl + "documents/Thumbnail/" + match[1] + "?size=128";
          }
          // Unrecognised format — fall back to the raw URL (older absolute/relative path).
          const noTrail = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          return noTrail + defaultUrl;
        }

        // 2. No default chosen — use the first VehiclePhoto we have for this vehicle.
        //    Slim DTO ships defaultImageId for this (lowest Position).
        if (item.vehicle?.defaultImageId) {
          return baseUrl + "documents/Thumbnail/" + item.vehicle.defaultImageId + "?size=128";
        }

        // 3. Legacy fallback: vehiclePhotoList directly (older API response shape).
        if (item.vehicle?.vehiclePhotoList && item.vehicle.vehiclePhotoList.length > 0) {
          return baseUrl + "documents/Thumbnail/" + item.vehicle.vehiclePhotoList[0].documentId + "?size=128";
        }

        return "";
      } catch (error) {
        console.error('Error getting image:', error);
        return "";
      }
    },

    getPricing(item) {
      if (item.pricing == null) return "N/A";
      if (item.pricing.recommendedSalePrice == 0) return "N/A";
      return new Intl.NumberFormat("ms-MY", {
        style: "currency",
        currency: "MYR",
      }).format(item.pricing.recommendedSalePrice);
    },

    // Process Status Check Methods
    getApMitiStatus(item) {
      try {
        const completed = item.adminitrativeCost?.apCostAmount !== null && item.adminitrativeCost?.apCostAmount !== undefined && item.adminitrativeCost?.apCostAmount > 0;
        return {
          completed,
          message: completed ? "AP amount entered" : "Pending AP amount"
        };
      } catch (error) {
        return { completed: false, message: "Pending AP amount" };
      }
    },

    getBlStatus(item) {
      try {
        const hasDocuments = item.import?.billOfLandingDocuments?.length > 0;
        return {
          completed: hasDocuments,
          message: hasDocuments ? "BOL/BL uploaded" : "Pending BOL/BL upload"
        };
      } catch (error) {
        return { completed: false, message: "Pending BOL/BL upload" };
      }
    },

    getJk69Status(item) {
      try {
        const hasDocuments = item.clearance?.exportCertificateDocuments?.length > 0;
        return {
          completed: hasDocuments,
          message: hasDocuments ? "SIJIL EXPORT uploaded" : "Pending SIJIL EXPORT upload"
        };
      } catch (error) {
        return { completed: false, message: "Pending SIJIL EXPORT upload" };
      }
    },

    getK1K8Status(item) {
      try {
        const hasK1 = item.clearance?.k1Documents?.length > 0;
        const hasK8 = item.clearance?.k8Documents?.length > 0;
        const completed = hasK1 || hasK8;
        return {
          completed,
          message: completed ?
            (hasK1 && hasK8 ? "K1 & K8 uploaded" :
             hasK1 ? "K1 uploaded" : "K8 uploaded") :
            "Pending K1 & K8 upload"
        };
      } catch (error) {
        return { completed: false, message: "Pending K1 & K8 upload" };
      }
    },

    getFloorStockStatus(item) {
      try {
        const expense = item.expense;
        if (!expense) {
          return { completed: false, message: "Pending expense amounts" };
        }

        const allEntered =
          expense.serviceEzCareCostAmount !== null && expense.serviceEzCareCostAmount !== undefined && expense.serviceEzCareCostAmount > 0 &&
          expense.interiorCostAmount !== null && expense.interiorCostAmount !== undefined && expense.interiorCostAmount > 0 &&
          expense.paintCostAmount !== null && expense.paintCostAmount !== undefined && expense.paintCostAmount > 0 &&
          expense.tyreCostAmount !== null && expense.tyreCostAmount !== undefined && expense.tyreCostAmount > 0 &&
          expense.rentalCostAmount !== null && expense.rentalCostAmount !== undefined && expense.rentalCostAmount > 0;

        return {
          completed: allEntered,
          message: allEntered ? "All expenses entered" : "Pending expense amounts"
        };
      } catch (error) {
        return { completed: false, message: "Pending expense amounts" };
      }
    },

    getTtStatus(item) {
      try {
        const intFsEntered = item.adminitrativeCost?.intFsCostAmount !== null && item.adminitrativeCost?.intFsCostAmount !== undefined && item.adminitrativeCost?.intFsCostAmount > 0;
        const forwardingEntered = item.adminitrativeCost?.forwardingCostAmount !== null && item.adminitrativeCost?.forwardingCostAmount !== undefined && item.adminitrativeCost?.forwardingCostAmount > 0;
        const completed = intFsEntered && forwardingEntered;
        return {
          completed,
          message: completed ? "Int FS & Forwarding entered" :
                   !intFsEntered && !forwardingEntered ? "Pending Int FS & Forwarding" :
                   !intFsEntered ? "Pending Int FS amount" : "Pending Forwarding amount"
        };
      } catch (error) {
        return { completed: false, message: "Pending Int FS & Forwarding" };
      }
    },

    getDutyTaxStatus(item) {
      try {
        const completed = item.adminitrativeCost?.dutyCostAmount !== null && item.adminitrativeCost?.dutyCostAmount !== undefined && item.adminitrativeCost?.dutyCostAmount > 0;
        return {
          completed,
          message: completed ? "Duty Tax entered" : "Pending Duty Tax"
        };
      } catch (error) {
        return { completed: false, message: "Pending Duty Tax" };
      }
    },

    getPuspakomStatus(item) {
      try {
        const hasDocuments = item.registration?.puspakomB2SlipDocuments?.length > 0;
        return {
          completed: hasDocuments,
          message: hasDocuments ? "Puspakom B2 uploaded" : "Pending Puspakom B2 upload"
        };
      } catch (error) {
        return { completed: false, message: "Pending Puspakom B2 upload" };
      }
    },

    getRegisterStatus(item) {
      try {
        const hasDocuments = item.registration?.jpjGeranDocuments?.length > 0;
        return {
          completed: hasDocuments,
          message: hasDocuments ? "Vehicle Reg. Cert. uploaded" : "Pending Vehicle Reg. Cert. upload"
        };
      } catch (error) {
        return { completed: false, message: "Pending Vehicle Reg. Cert. upload" };
      }
    },

    getBrandName(item) {
      if (item.vehicle == null) return "N/A";
      if (item.vehicle.brand == null) return "N/A";
      return item.vehicle.brand.name;
    },

    getModelName(item) {
      if (item.vehicle == null) return "N/A";
      if (item.vehicle.model == null) return "N/A";
      return item.vehicle.model.name;
    },

    getLatesStockStatusName(item) {
      if (item.latestStockStatus == null) return "N/A";
      if (item.latestStockStatus.stockStatus == null) return "N/A";
      return item.latestStockStatus.stockStatus.name;
    },

    async downloadCsvFiltered() {
      await this._exportCsv("filtered", {
        filters: this.columnFilterValue,
        priceMin: this.priceMinFilter,
        priceMax: this.priceMaxFilter,
        monthFrom: this.monthFromFilter,
        yearFrom: this.yearFromFilter,
        monthTo: this.monthToFilter,
        yearTo: this.yearToFilter,
      }, "stocks-filtered");
    },
    async downloadCsvAll() {
      await this._exportCsv("all", {}, "stocks-all");
    },
    async _exportCsv(mode, params, filenamePrefix) {
      this.downloadingCsv = mode;
      try {
        // Lightweight endpoint — projects only the 5 columns we need (no Includes,
        // no Documents/images bytea). Much faster than getList for >100 rows.
        const response = await this.api.export(params);
        const items = (response && response.result && response.result.items) || [];
        const csv = this._buildCsv(items);
        this._triggerCsvDownload(csv, `${filenamePrefix}-${moment().format("YYYYMMDD-HHmmss")}.csv`);
        this.toast("Success", `Downloaded ${items.length} stocks`, "success");
      } catch (e) {
        this.toast("Error", "Failed to download CSV", "danger");
      } finally {
        this.downloadingCsv = null;
      }
    },
    _buildCsv(items) {
      // 13-column layout matching references/stocks (1).csv:
      // No, Stock No, Year, Loc, Model, Specification, Color, Chassis No,
      // SPLR, E.T.A, Status, Date, Salesman.
      const header = [
        "No", "Stock No", "Year", "Loc", "Model", "Specification",
        "Color", "Chassis No", "SPLR", "E.T.A", "Status", "Date", "Salesman",
      ];
      const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const formatDate = (d) => {
        if (!d) return "";
        const out = formatMalaysiaDate(d);
        // formatMalaysiaDate returns "—" for empty — strip that for CSV.
        return out === "—" ? "" : (out || "");
      };
      // Build the multi-state Status string the same way the UI table
      // does — one Stock can be flagged with several states at once
      // (e.g. "Available, LOU"). Order matches the badge order in the
      // table: Available / LOU / Booking / Sold / Cancelled / Request Duty.
      const statusFromFlags = (item) => {
        const states = [];
        if (item.isOpen) states.push("Available");
        if (item.isLou) states.push("LOU");
        if (item.isBooked) states.push("Booking");
        if (item.isSold) states.push("Sold");
        if (item.isCancelled) states.push("Cancelled");
        if (item.isRequestDuty) states.push("Request Duty");
        return states.join(", ");
      };
      const rows = items.map((item, i) => [
        i + 1,
        item.stockNo || "",
        item.year || "",
        item.lotNo || "",
        [item.brand, item.model].filter(Boolean).join(" "),
        item.description || "",
        item.color || "",
        item.chasisNo || "",
        item.supplierName || "",
        formatDate(item.eta),
        statusFromFlags(item),
        formatDate(item.bookingDate),
        item.salesmanName || "",
      ].map(escape).join(","));
      return "﻿" + [header.join(","), ...rows].join("\r\n");
    },
    _triggerCsvDownload(csvString, filename) {
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },

    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration (3000ms + small buffer)
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 3500);
    },

    toggleDetails(item, index) {
      const isExpanding = !item._toggled;

      if (isExpanding) {
        // Start timing immediately after row click
        const clickTime = Date.now();
        console.log(`[Row Expansion] Stock ID: ${item.id}, Click Time: ${new Date(clickTime).toISOString()}`);

        // Show loading spinner immediately
        this.expandingItemId = item.id;
        const spinnerSetTime = Date.now();
        console.log(`[Row Expansion] Spinner set at +${spinnerSetTime - clickTime}ms`);

        // Use requestAnimationFrame to defer the heavy DOM updates
        // This ensures the spinner renders first before blocking the main thread
        requestAnimationFrame(() => {
          const rafStartTime = Date.now();
          console.log(`[Row Expansion] requestAnimationFrame start at +${rafStartTime - clickTime}ms`);

          // Now toggle the row expansion
          this.$set(item, "_toggled", true);
          // Reset details ready flag
          this.$set(item, "_detailsReady", false);
          const toggledSetTime = Date.now();
          console.log(`[Row Expansion] _toggled set to true at +${toggledSetTime - clickTime}ms`);

          // Wait for initial DOM update, then render details content
          this.$nextTick(() => {
            const nextTickTime = Date.now();
            console.log(`[Row Expansion] First $nextTick at +${nextTickTime - clickTime}ms`);

            // Short delay before rendering heavy content
            setTimeout(() => {
              const lazyRenderTime = Date.now();
              console.log(`[Row Expansion] Starting lazy render at +${lazyRenderTime - clickTime}ms`);

              // Mark details as ready to render
              this.$set(item, "_detailsReady", true);

              this.$nextTick(() => {
                const finalNextTickTime = Date.now();
                console.log(`[Row Expansion] Final $nextTick completed at +${finalNextTickTime - clickTime}ms`);

                requestAnimationFrame(() => {
                  const finalRafTime = Date.now();
                  console.log(`[Row Expansion] Final requestAnimationFrame at +${finalRafTime - clickTime}ms`);

                  // Keep spinner visible for a brief moment after rendering
                  const minTotalDisplayTime = 200;
                  const elapsed = Date.now() - clickTime;
                  const remaining = Math.max(0, minTotalDisplayTime - elapsed);
                  console.log(`[Row Expansion] Elapsed: ${elapsed}ms, Remaining for min display: ${remaining}ms`);

                  // Hide spinner after ensuring minimum display time
                  setTimeout(() => {
                    const spinnerHideTime = Date.now();
                    this.expandingItemId = null;
                    console.log(`[Row Expansion] Spinner hidden at +${spinnerHideTime - clickTime}ms`);
                    console.log(`[Row Expansion] Total expansion time: ${spinnerHideTime - clickTime}ms\n`);
                  }, remaining);
                });
              });
            }, 16); // One frame delay (16ms) for smoother animation
          });
        });
      } else {
        // Collapsing - no loading needed
        const collapseTime = Date.now();
        console.log(`[Row Collapse] Stock ID: ${item.id}, Collapse Time: ${new Date(collapseTime).toISOString()}\n`);
        this.$set(item, "_toggled", false);
        this.$set(item, "_detailsReady", false);
      }

      this.collapseDuration = 300;
      this.$nextTick(() => {
        this.collapseDuration = 0;
      });
    },

    refreshTable() {
      // Start timing for data load
      const loadStartTime = Date.now();
      console.log(`\n[Data Load] Start Time: ${new Date(loadStartTime).toISOString()}`);
      console.log(`[Data Load] Page: ${this.activePage}, ItemsPerPage: ${this.itemsPerPage}`);

      // Clear existing data first for better loading UX
      this.loadedItems = [];
      this.loading = true;
      const clearDataTime = Date.now();
      console.log(`[Data Load] Data cleared at +${clearDataTime - loadStartTime}ms`);

      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        sorter: this.sorterValue,
        filters: this.columnFilterValue,
        search: this.tableFilterValue,
        priceMin: this.priceMinFilter,
        priceMax: this.priceMaxFilter,
        monthFrom: this.monthFromFilter,
        yearFrom: this.yearFromFilter,
        monthTo: this.monthToFilter,
        yearTo: this.yearToFilter,
      };

      const apiCallTime = Date.now();
      console.log(`[Data Load] API call started at +${apiCallTime - loadStartTime}ms`);

      this.api
        .getList(params)
        .then((response) => {
          const apiResponseTime = Date.now();
          console.log(`[Data Load] API response received at +${apiResponseTime - loadStartTime}ms`);

          if (response.result) {
            this.loadedItems = response.result.items;
            this.totalItems = response.result.totalItems;
            this.pages = Math.ceil(
              response.result.totalItems / this.itemsPerPage
            );
            const dataAssignedTime = Date.now();
            console.log(`[Data Load] Data assigned (${response.result.items.length} items) at +${dataAssignedTime - loadStartTime}ms`);

            // Update the cached computed items immediately after loading data
            this.updateComputedItemsCache();
            const cacheCompleteTime = Date.now();
            console.log(`[Data Load] Cache updated at +${cacheCompleteTime - loadStartTime}ms`);
          } else {
            this.loadedItems = [];
            this.totalItems = 0;
            this.pages = 1;
            this.cachedComputedItems = [];
            console.log(`[Data Load] No data received`);
          }
          this.loading = false;

          // Wait for Vue to process the data and render
          this.$nextTick(() => {
            const nextTickTime = Date.now();
            console.log(`[Data Load] $nextTick (Vue rendering complete) at +${nextTickTime - loadStartTime}ms`);

            requestAnimationFrame(() => {
              const rafTime = Date.now();
              console.log(`[Data Load] requestAnimationFrame (browser paint complete) at +${rafTime - loadStartTime}ms`);
              console.log(`[Data Load] Total load time: ${rafTime - loadStartTime}ms\n`);
            });
          });
        })
        .catch((error) => {
          const errorTime = Date.now();
          console.error(`[Data Load] Error at +${errorTime - loadStartTime}ms:`, error);
          this.toast("Error", "Failed to load data", "danger");
          this.loading = false;
        });
    },

    onItemsPerPageChange(newValue) {
      const self = this;

      // Safety warning for "Show All" with large datasets
      if (newValue === 999999 && self.totalItems > 1000) {
        const proceed = confirm(
          `Loading all ${self.totalItems} items may be slow and affect performance.\n\nContinue?`
        );

        if (!proceed) {
          // Reset to safe value
          self.$nextTick(() => {
            self.itemsPerPage = 1000;
          });
          return;
        }
      }

      // Reset to first page when changing items per page
      self.activePage = 1;

      // Refresh the table with new items per page
      self.refreshTable();
    },

    onEdit(item) {
      this.$router.push({
        path: `/admins/Stock/${item.id}`,
      });
    },

    // Opens the same Stock detail page in a new browser tab. Useful when the
    // user wants to keep the filtered list in place and review multiple stocks
    // in parallel. `$router.resolve` builds the full hash URL so it works
    // regardless of base path / deployment.
    onEditInNewTab(item) {
      const route = this.$router.resolve({ path: `/admins/Stock/${item.id}` });
      window.open(route.href, "_blank", "noopener");
    },

    onDeleteConfirmation() {
      this.api
        .delete(this.itemToDelete.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "Stock deleted successfully", "success");
          this.warningModal = false;
          this.itemToDelete = {};
        })
        .catch(({ data }) => {
          this.toast("Error", "Failed to delete stock", "danger");
          this.warningModal = false;
          this.itemToDelete = {};
        });
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    addNew() {
      this.$router.push({ path: "/admins/Stock" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.stock-list-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Active Filters Section */
.active-filters-section {
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.filters-header h6 {
  margin: 0;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
}

.filters-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.25rem;
}

.filter-close {
  margin-left: 0.5rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.filter-close:hover {
  opacity: 1;
}

/* Enhanced Filter Card */
.filter-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.filter-header {
  border-bottom: 1px solid var(--cui-border-color, #d8dbe0);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.header-content {
  display: flex;
  align-items: center;
}

.filter-count {
  font-size: 0.8125rem;
  font-weight: 500;
}

.filter-body {
  padding: 1.5rem;
}

/* Filter Sections */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section:last-of-type {
  margin-bottom: 0;
}

.filter-section-title {
  color: #4f5d73;
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
}

.filter-section-title i {
  color: #768192;
  font-size: 0.875rem;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Enhanced Stock List Card */
.stock-list-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.list-header {
  border-bottom: 1px solid var(--cui-border-color, #d8dbe0);
}

.status-summary {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-badge {
  font-size: 0.8125rem;
  font-weight: 500;
}

.info-tooltip {
  cursor: help;
}

/* Mobile Card View */
.mobile-view {
  padding: 1rem;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: var(--cui-card-bg, #fff);
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.mobile-card-header {
  margin-bottom: 0.75rem;
}

.mobile-image {
  flex-shrink: 0;
}

.stock-image {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.no-image {
  width: 60px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cui-body-bg, #f8f9fa);
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  color: var(--cui-text-muted, #6c757d);
}

.mobile-card-body {
  margin-bottom: 0.75rem;
}

.mobile-status-badge,
.mobile-state-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.mobile-card-footer {
  padding-top: 0.75rem;
  border-top: 1px solid var(--cui-border-color-translucent, rgba(0, 0, 0, 0.125));
  text-align: center;
}

.mobile-details {
  background: var(--cui-body-bg, #f8f9fa);
  margin: 0 -1rem -1rem -1rem;
  padding: 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

.mobile-details-content {
  background: var(--cui-card-bg, #fff);
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.mobile-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr:hover {
  cursor: pointer;
}

/* Professional Badge Styling */
.state-badge,
.status-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

/* Details Section */
.details-wrapper {
  padding: 1rem;
  background: var(--cui-body-bg, #f8f9fa);
}

.details-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Document Section Styling */
.document-section h6 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.75rem;
}

.document-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-links {
  margin-top: 0.5rem;
}

.document-links .btn-sm {
  font-size: 0.8125rem;
  padding: 0.375rem 0.75rem;
  white-space: nowrap;
}

.detail-car-image {
  width: 100%;
  max-width: 200px;
  height: auto;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Table Thumbnail Styling */
.table-thumbnail {
  width: 70px;
  height: 52px;
  object-fit: cover;
  border-radius: 0.25rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  cursor: pointer;
  transition: transform 0.2s ease;
}

.table-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
}

.no-thumbnail {
  width: 70px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cui-body-bg, #f8f9fa);
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.25rem;
}

.no-thumbnail i {
  font-size: 1.25rem;
  opacity: 0.5;
}

.action-buttons {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Pagination */
.pagination-wrapper {
  padding: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Enhanced Footer */
.card-footer {
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
  background: var(--cui-body-bg, #f8f9fa);
}

.footer-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.footer-info {
  display: flex;
  align-items: center;
}

/* Modal Enhancements */
.modal-content-wrapper {
  padding: 1rem 0;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3.75rem 1.25rem;
}

/* Mobile Responsiveness */
@media (max-width: 767.98px) {
  .stock-list-wrapper {
    padding: 1rem;
  }

  .filter-body {
    padding: 1rem;
  }

  .filter-section {
    margin-bottom: 1.25rem;
  }

  .filter-section-title {
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
  }

  .filter-actions {
    flex-direction: column;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .filter-actions .btn {
    width: 100%;
  }
  
  .status-summary {
    flex-direction: column;
    gap: 0.375rem;
  }
  
  .list-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.75rem;
  }
  
  .footer-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .footer-actions .btn {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .stock-list-wrapper {
    padding: 0.75rem;
  }
  
  .filter-body {
    padding: 1rem;
  }
  
  .list-header {
    padding: 1rem;
  }
  
  .mobile-card {
    padding: 0.75rem;
  }
  
  .stock-image {
    width: 50px;
    height: 38px;
  }
  
  .no-image {
    width: 50px;
    height: 38px;
  }
}

/* Print Styles */
@media print {
  .stock-list-wrapper {
    padding: 0;
  }
  
  .filter-card {
    display: none;
  }
  
  .mobile-view {
    display: none;
  }
  
  .desktop-view {
    display: block !important;
  }
  
  .professional-table {
    font-size: 0.75rem;
  }
  
  .card-footer {
    display: none;
  }
}

/* Process Status Section */
.process-status-section h6 {
  color: #4f5d73;
  font-weight: 600;
  font-size: 0.9rem;
}

.process-status-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.process-status-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.8;
}

.status-label {
  font-weight: 500;
  color: #4f5d73;
  min-width: 110px;
}

.status-badge {
  font-size: 11px !important;
  padding: 3px 8px !important;
  font-weight: 600;
}

.status-description {
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}

@media (max-width: 768px) {
  .process-status-row {
    flex-wrap: wrap;
  }

  .status-label {
    min-width: 90px;
    font-size: 12px;
  }

  .status-badge {
    font-size: 10px !important;
    padding: 2px 6px !important;
  }

  .status-description {
    flex: 1 0 100%;
    margin-left: 0;
    font-size: 11px;
    padding-left: 90px;
  }
}
</style>